// 261010 P1b (luồng Sky) — chip engine + khối gập "Giai đoạn" của MỘT event. Logic thuần ở skyVideoMetaUtils.ts.
import React, { useState } from 'react';
import { Button, Collapse, Popconfirm, Tag, Tooltip } from 'antd';
import type { FilmEvent, FilmFlowVersion, FilmVideoEngine } from '../../types/film';
import { SKY_GEN_NOTE, skyEventView, SkyTag } from './skyVideoMetaUtils';

const TONE_COLOR: Record<SkyTag['tone'], string> = { warn: 'orange', info: 'blue', error: 'red' };

type EngineChangeResult = 'changed' | 'promptEdited' | 'needsSplit' | 'failed';

/** Chip engine + giây gen + các tag cờ — đặt cạnh đầu thẻ event. Plan không phải sky ⇒ không render gì. */
export const SkyEngineChips: React.FC<{
  ev: FilmEvent;
  flowVersion?: FilmFlowVersion | null;
  onChangeEngine?: (eventId: string, engine: FilmVideoEngine, acceptEditedPrompt?: boolean) => Promise<EngineChangeResult>;
  /** P1c-1: modal thuộc parent để giữ preview/reload state cùng plan. */
  onOpenDowngrade?: (eventId: string) => void;
}> = ({ ev, flowVersion, onChangeEngine, onOpenDowngrade }) => {
  const v = skyEventView(ev, flowVersion);
  const [changing, setChanging] = useState(false);
  const [confirmEditedPrompt, setConfirmEditedPrompt] = useState(false);
  if (!v) return null;
  const nextEngine: FilmVideoEngine | null = ev.videoEngine === 'SEEDANCE_25'
    ? 'SEEDANCE_20'
    : ev.videoEngine === 'SEEDANCE_20' ? 'SEEDANCE_25' : null;
  const change = async (acceptEditedPrompt = false) => {
    if (!nextEngine || !onChangeEngine) return;
    setChanging(true);
    try {
      const result = await onChangeEngine(ev.id, nextEngine, acceptEditedPrompt);
      if (result === 'promptEdited') setConfirmEditedPrompt(true);
      if (result === 'changed') setConfirmEditedPrompt(false);
      if (result === 'needsSplit') onOpenDowngrade?.(ev.id);
    } finally {
      setChanging(false);
    }
  };
  const switchButton = nextEngine && onChangeEngine ? (
    <Button size="small" loading={changing}>→ {nextEngine === 'SEEDANCE_25' ? '2.5' : '2.0'}</Button>
  ) : null;
  return (
    <>
      {v.engineLabel && (
        <Tooltip title={<div>{v.reason || 'Đề xuất engine'}{v.secondsLabel && <div style={{ opacity: 0.75, marginTop: 4 }}>{v.secondsLabel}</div>}<div style={{ opacity: 0.75, marginTop: 4 }}>{SKY_GEN_NOTE}</div></div>}>
          <Tag color={v.engineLabel === '2.5' ? 'purple' : 'geekblue'} style={{ margin: 0 }}>
            🎞 {v.engineLabel}{typeof ev.genDurationS === 'number' ? ` · gen ${ev.genDurationS}s` : ''}
          </Tag>
        </Tooltip>
      )}
      {switchButton && (confirmEditedPrompt ? (
        <Popconfirm
          open
          title="Prompt Kỹ thuật đã sửa tay mang mốc giây cũ. Giữ nguyên và vẫn đổi engine?"
          okText="Giữ và đổi"
          cancelText="Huỷ"
          okButtonProps={{ loading: changing }}
          onConfirm={() => change(true)}
          onCancel={() => setConfirmEditedPrompt(false)}
        >
          {switchButton}
        </Popconfirm>
      ) : (
        <Popconfirm
          title={`Đổi engine video sang ${nextEngine === 'SEEDANCE_25' ? '2.5' : '2.0'}?`}
          okText="Đổi engine"
          cancelText="Huỷ"
          onConfirm={() => change(false)}
        >
          {switchButton}
        </Popconfirm>
      ))}
      {v.tags.map((t) => (
        <Tooltip key={t.key} title={t.tip}><Tag color={TONE_COLOR[t.tone]} style={{ margin: 0 }}>{t.text}</Tag></Tooltip>
      ))}
    </>
  );
};

/** Khối gập "Giai đoạn": số, mục đích, nhịp/thoại, ✂ ở ranh giới cắt được. */
export const SkyPhasesBlock: React.FC<{ ev: FilmEvent; flowVersion?: FilmFlowVersion | null }> = ({ ev, flowVersion }) => {
  const v = skyEventView(ev, flowVersion);
  if (!v || !v.phases.length) return null;
  const meta = ev.videoMeta;
  return (
    <Collapse size="small" ghost style={{ marginTop: 6 }} items={[{
      key: 'sky-phases',
      label: <span style={{ fontSize: 12 }}>🧩 Giai đoạn ({v.phases.length}){v.stale ? ' · đã cũ' : ''}</span>,
      children: (
        <div style={{ fontSize: 12 }}>
          {meta?.dramaticGoal && <div style={{ marginBottom: 4 }}><b>Mục tiêu:</b> {meta.dramaticGoal}</div>}
          {meta?.boundaryReason && <div style={{ marginBottom: 4, opacity: 0.8 }}><b>Mở video vì:</b> {meta.boundaryReason}</div>}
          {v.phases.map((p) => (
            <div key={p.order} style={{ marginBottom: 6 }}>
              <div><b>{p.order}.</b> {p.purpose}</div>
              <ul style={{ margin: '2px 0 2px 18px', padding: 0 }}>
                {p.beats.map((b) => <li key={b.order}>{b.text}</li>)}
                {p.lines.map((l, i) => <li key={`l${i}`} style={{ opacity: 0.85 }}>🗣 {l.voiceOver ? '(V.O.) ' : ''}{l.speaker ? `${l.speaker}: ` : ''}{l.text}</li>)}
              </ul>
              <div style={{ opacity: 0.7 }}>↳ {p.endState}</div>
              {p.cutAfterAllowed && (
                <Tooltip title={p.cutAfterReason || 'Cắt được ở đây'}><span style={{ color: '#fa8c16' }}>✂ cắt được sau giai đoạn {p.order}</span></Tooltip>
              )}
            </div>
          ))}
          {v.unanchoredLines > 0 && <div style={{ color: '#fa8c16' }}>{v.unanchoredLines} câu thoại chưa neo nhịp — không gán được vào giai đoạn nào.</div>}
        </div>
      ),
    }]} />
  );
};
